/**
 * SortBuilder Component
 *
 * Container for multi-field sorting configuration with drag-to-reorder.
 * Allows users to add up to 5 sort fields and control priority via drag-and-drop.
 */

import { Button, Space, Typography } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { SortRow } from './SortRow'
import type { SortField } from '../hooks/useSorting'
import type { FieldMetadata } from '@/services/metadataService'

export interface SortBuilderProps {
  /** Entity model name */
  model: string

  /** Pre-fetched field metadata (avoids metadata API calls) */
  fields?: Record<string, FieldMetadata>

  /** Current sort fields */
  sortFields: SortField[]

  /** Callback when sort field is added */
  onAddSort: () => void

  /** Callback when sort field is removed */
  onRemoveSort: (id: string) => void

  /** Callback when sort field is updated */
  onUpdateSort: (id: string, updates: Partial<Omit<SortField, 'id'>>) => void

  /** Callback when sort fields are reordered */
  onReorderSort: (startIndex: number, endIndex: number) => void
}

/**
 * Sortable wrapper for SortRow (needed by dnd-kit)
 */
function SortableRow({
  sort,
  model,
  fields,
  index,
  onUpdate,
  onRemove,
}: {
  sort: SortField
  model: string
  fields?: Record<string, FieldMetadata>
  index: number
  onUpdate: (updates: Partial<Omit<SortField, 'id'>>) => void
  onRemove: () => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sort.id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div ref={setNodeRef} style={style}>
      <SortRow
        sort={sort}
        model={model}
        fields={fields}
        onChange={onUpdate}
        onRemove={onRemove}
        index={index}
        dragHandleProps={{ ...attributes, ...listeners }}
      />
    </div>
  )
}

export function SortBuilder({
  model,
  fields,
  sortFields,
  onAddSort,
  onRemoveSort,
  onUpdateSort,
  onReorderSort,
}: SortBuilderProps) {
  // Setup dnd-kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // Handle drag end
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = sortFields.findIndex((s) => s.id === active.id)
      const newIndex = sortFields.findIndex((s) => s.id === over.id)

      onReorderSort(oldIndex, newIndex)
    }
  }

  const maxReached = sortFields.length >= 5

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {/* Sort Rows with Drag-and-Drop */}
      {sortFields.length > 0 && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={sortFields.map((s) => s.id)} strategy={verticalListSortingStrategy}>
            <div style={{ width: '100%' }}>
              {sortFields.map((sort, index) => (
                <SortableRow
                  key={sort.id}
                  sort={sort}
                  model={model}
                  fields={fields}
                  index={index}
                  onUpdate={(updates) => onUpdateSort(sort.id, updates)}
                  onRemove={() => onRemoveSort(sort.id)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Subtle status text for multiple sort fields */}
      {sortFields.length > 1 && (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Sorting by {sortFields.length} fields (priority: top to bottom)
        </Typography.Text>
      )}

      {/* Add Sort Button */}
      <Button
        type="dashed"
        icon={<PlusOutlined />}
        onClick={onAddSort}
        block
        disabled={maxReached}
      >
        {maxReached ? 'Maximum 5 sort fields' : 'Add Sort Field'}
      </Button>
    </Space>
  )
}
